import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { Badge } from '../ui/Badge';
import { ThemeToggle } from './ThemeToggle';
import { useModal } from '../ui/Modal';
import { useToast } from '../ui/Toast';

export interface HeaderProps {
  title?: string | undefined;
  environment?: 'live' | 'sandbox' | string;
  onMobileMenuToggle?: (() => void) | undefined;
  className?: string | undefined;
}

export function Header({
  title = 'Dashboard',
  environment = 'live',
  onMobileMenuToggle,
  className = '',
}: HeaderProps) {
  const { user, activeOrgId, organisations, logout } = useAuth();
  const navigate = useNavigate();
  const { openModal } = useModal();
  const { addToast } = useToast();
  const [profileOpen, setProfileOpen] = useState(false);

  const activeOrg = organisations.find((o) => o.id === activeOrgId);

  const handleLogout = () => {
    setProfileOpen(false);
    openModal({
      title: 'Sign Out?',
      description: 'Are you sure you want to sign out of your account?',
      confirmText: 'Sign Out',
      cancelText: 'Cancel',
      variant: 'destructive',
      onConfirm: async () => {
        await logout();
        navigate('/login');
        addToast({ type: 'info', message: 'Signed out successfully' });
      },
    });
  };

  return (
    <header
      className={`sticky top-0 z-30 flex h-16 w-full items-center justify-between border-b border-line nav-blur px-4 sm:px-6 lg:px-8 ${className}`}
    >
      {/* Left section: Mobile menu button & Title */}
      <div className="flex items-center space-x-3">
        {onMobileMenuToggle && (
          <button
            type="button"
            onClick={onMobileMenuToggle}
            className="lg:hidden rounded-xl p-2 text-sub hover:bg-slate-500/10 hover:text-ink transition cursor-pointer"
            aria-label="Open mobile navigation"
          >
            <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
            </svg>
          </button>
        )}

        <div className="flex items-center space-x-3">
          <h1 className="text-lg font-bold tracking-tight text-ink">{title}</h1>
          <Badge variant={environment === 'live' ? 'emerald' : 'warn'} size="sm">
            {environment.toUpperCase()}
          </Badge>
          {activeOrg && (
            <span className="hidden sm:inline-flex text-xs font-medium text-sub bg-slate-500/10 px-2.5 py-1 rounded-full border border-line">
              {activeOrg.name}
            </span>
          )}
        </div>
      </div>

      {/* Right section: ThemeToggle & User Profile dropdown */}
      <div className="flex items-center space-x-3">
        <ThemeToggle size="sm" />

        {user && (
          <div className="relative">
            <button
              type="button"
              onClick={() => setProfileOpen(!profileOpen)}
              className="flex items-center space-x-2 rounded-xl border border-line bg-card px-2.5 py-1.5 hover:bg-slate-500/10 transition cursor-pointer"
              aria-expanded={profileOpen}
              aria-haspopup="true"
            >
              <div className="h-7 w-7 rounded-full bg-gradient-to-tr from-blue-600 to-indigo-600 text-white font-bold text-xs flex items-center justify-center">
                {(user.fullName || user.email)[0]?.toUpperCase()}
              </div>
              <span className="hidden md:inline-block text-xs font-semibold text-ink max-w-[120px] truncate">
                {user.fullName || user.email}
              </span>
              <span className="text-sub text-[10px]" aria-hidden="true">
                ▼
              </span>
            </button>

            {profileOpen && (
              <div className="absolute right-0 z-50 mt-2 w-56 rounded-xl border border-line bg-card p-2 shadow-cardhover space-y-1">
                <div className="px-3 py-2 border-b border-line">
                  <p className="text-xs font-bold text-ink truncate">
                    {user.fullName || 'User'}
                  </p>
                  <p className="text-[10px] text-sub truncate">{user.email}</p>
                </div>
                <button
                  type="button"
                  onClick={() => {
                    setProfileOpen(false);
                    navigate('/dashboard/settings');
                  }}
                  className="w-full text-left px-3 py-2 text-xs text-sub hover:bg-slate-500/10 hover:text-ink rounded-lg transition"
                >
                  Settings
                </button>
                <button
                  type="button"
                  onClick={handleLogout}
                  className="w-full text-left px-3 py-2 text-xs text-err hover:bg-red-500/10 rounded-lg transition font-medium"
                >
                  Sign Out
                </button>
              </div>
            )}
          </div>
        )}
      </div>
    </header>
  );
}
